import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import axios from 'axios';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '../../components/ui/dialog';
import { Badge } from '../../components/ui/badge';
import { Card } from '../../components/ui/card';
import { Pagination, PaginationContent, PaginationItem, PaginationLink, PaginationNext, PaginationPrevious, PaginationEllipsis } from '../../components/ui/pagination';
import { Home, Plus, Edit, Trash2, Eye } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';
const PAGE_SIZE = 10;

export default function AdminVideos() {
  const navigate = useNavigate();
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingVideo, setEditingVideo] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [formData, setFormData] = useState({
    title: '',
    dek: '',
    platform: 'youtube',
    videoId: '',
    series: 'other',
    durationSec: '',
    thumbnailUrl: '',
    tags: '',
    status: 'draft'
  });
  const [uploadingImage, setUploadingImage] = useState(false);

  const token = localStorage.getItem('admin_token');

  useEffect(() => {
    if (!token) {
      navigate('/admin/login');
      return;
    }
    fetchVideos(currentPage);
  }, [token, navigate, currentPage]);

  const fetchVideos = async (page) => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_URL}/api/admin/videos?page=${page}&pageSize=${PAGE_SIZE}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Handle both paginated and non-paginated response formats
      if (response.data && response.data.items) {
        setVideos(response.data.items || []);
        setTotalPages(response.data.totalPages || 1);
        setTotalItems(response.data.total || 0);
      } else if (Array.isArray(response.data)) {
        setVideos(response.data);
        setTotalPages(1);
        setTotalItems(response.data.length);
      } else {
        setVideos([]);
        setTotalPages(1);
        setTotalItems(0);
      }
    } catch (error) {
      console.error('Error fetching videos:', error);
      toast.error('Failed to load videos');
      setVideos([]);
      if (error.response?.status === 401) {
        navigate('/admin/login');
      }
    } finally {
      setLoading(false);
    }
  };

  const handlePageChange = (page) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...formData,
        durationSec: formData.durationSec ? parseInt(formData.durationSec) : null,
        tags: formData.tags.split(',').map(t => t.trim()).filter(Boolean)
      };

      if (editingVideo) {
        await axios.put(`${API_URL}/api/videos/${editingVideo.id}`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Video updated successfully');
      } else {
        await axios.post(`${API_URL}/api/videos`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Video created successfully');
      }

      setDialogOpen(false);
      resetForm();
      fetchVideos(currentPage);
    } catch (error) {
      console.error('Error saving video:', error);
      toast.error(error.response?.data?.detail || 'Failed to save video');
    }
  };

  const handleEdit = (video) => {
    setEditingVideo(video);
    setFormData({
      title: video.title,
      dek: video.dek,
      platform: video.platform,
      videoId: video.videoId,
      series: video.series,
      durationSec: video.durationSec?.toString() || '',
      thumbnailUrl: video.thumbnailUrl,
      tags: video.tags.join(', '),
      status: video.status
    });
    setDialogOpen(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this video?')) return;

    try {
      await axios.delete(`${API_URL}/api/videos/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Video deleted successfully');
      fetchVideos(currentPage);
    } catch (error) {
      console.error('Error deleting video:', error);
      toast.error('Failed to delete video');
    }
  };

  const handleThumbnailUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    try {
      setUploadingImage(true);
      const formDataUpload = new FormData();
      formDataUpload.append('file', file);

      const response = await axios.post(`${API_URL}/api/upload/image`, formDataUpload, {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });

      setFormData({...formData, thumbnailUrl: response.data.url});
      toast.success('Thumbnail uploaded and compressed successfully!');
    } catch (error) {
      console.error('Error uploading thumbnail:', error);
      toast.error('Failed to upload thumbnail');
    } finally {
      setUploadingImage(false);
    }
  };

  const resetForm = () => {
    setEditingVideo(null);
    setFormData({
      title: '',
      dek: '',
      platform: 'youtube',
      videoId: '',
      series: 'other',
      durationSec: '',
      thumbnailUrl: '',
      tags: '',
      status: 'draft'
    });
  };

  // Generate page numbers to display
  const getPageNumbers = () => {
    const pages = [];
    const showPages = 5;
    
    let startPage = Math.max(1, currentPage - Math.floor(showPages / 2));
    let endPage = Math.min(totalPages, startPage + showPages - 1);
    
    if (endPage - startPage + 1 < showPages) {
      startPage = Math.max(1, endPage - showPages + 1);
    }
    
    for (let i = startPage; i <= endPage; i++) {
      pages.push(i);
    }
    
    return pages;
  };

  return (
    <>
      <Helmet>
        <title>Manage Videos · Admin</title>
      </Helmet>

      <div className="min-h-screen bg-background">
        <header className="sticky top-0 z-50 w-full border-b border-border bg-background/95 backdrop-blur">
          <div className="container max-w-[1200px] mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex h-16 items-center justify-between">
              <h1 className="font-display text-2xl font-bold">Videos</h1>
              <div className="flex items-center gap-2">
                <Button asChild variant="ghost" size="sm">
                  <Link to="/admin">
                    <Home className="mr-2 h-4 w-4" />
                    Dashboard
                  </Link>
                </Button>
              </div>
            </div>
          </div>
        </header>

        <main className="container max-w-[1200px] mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h2 className="font-display text-3xl font-bold mb-2">Manage Videos</h2>
              <p className="text-muted-foreground">
                {totalItems} total videos · Page {currentPage} of {totalPages}
              </p>
            </div>
            <Dialog open={dialogOpen} onOpenChange={(open) => {
              setDialogOpen(open);
              if (!open) resetForm();
            }}>
              <DialogTrigger asChild>
                <Button data-testid="new-video-btn">
                  <Plus className="mr-2 h-4 w-4" />
                  New Video
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle>{editingVideo ? 'Edit Video' : 'Create Video'}</DialogTitle>
                  <DialogDescription>Fill in the video details below</DialogDescription>
                </DialogHeader>
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div>
                    <Label htmlFor="title">Title</Label>
                    <Input
                      id="title"
                      value={formData.title}
                      onChange={(e) => setFormData({...formData, title: e.target.value})}
                      required
                    />
                  </div>
                  <div>
                    <Label htmlFor="dek">Description</Label>
                    <Textarea
                      id="dek"
                      value={formData.dek}
                      onChange={(e) => setFormData({...formData, dek: e.target.value})}
                      rows={2}
                      required
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="platform">Platform</Label>
                      <Select value={formData.platform} onValueChange={(value) => setFormData({...formData, platform: value})}>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="youtube">YouTube</SelectItem>
                          <SelectItem value="vimeo">Vimeo</SelectItem>
                          <SelectItem value="instagram">Instagram</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label htmlFor="videoId">
                        {formData.platform === 'instagram' ? 'Reel ID' : 'Video ID'}
                      </Label>
                      <Input
                        id="videoId"
                        value={formData.videoId}
                        onChange={(e) => setFormData({...formData, videoId: e.target.value})}
                        placeholder={formData.platform === 'instagram' ? 'e.g., ABC123xyz' : 'e.g., dQw4w9WgXcQ'}
                        required
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="series">Series</Label>
                      <Select value={formData.series} onValueChange={(value) => setFormData({...formData, series: value})}>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="podcast">Podcast</SelectItem>
                          <SelectItem value="brand-series">Brand Series</SelectItem>
                          <SelectItem value="other">Other</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label htmlFor="durationSec">Duration (seconds)</Label>
                      <Input
                        id="durationSec"
                        type="number"
                        value={formData.durationSec}
                        onChange={(e) => setFormData({...formData, durationSec: e.target.value})}
                        placeholder="e.g., 1800"
                      />
                    </div>
                  </div>
                  <div>
                    <Label htmlFor="thumbnailUrl">Thumbnail Image</Label>
                    <div className="flex gap-2 mt-1.5">
                      <Input
                        id="thumbnailUrl"
                        type="url"
                        value={formData.thumbnailUrl}
                        onChange={(e) => setFormData({...formData, thumbnailUrl: e.target.value})}
                        placeholder="https://... or upload below"
                        className="flex-1"
                        required
                      />
                      <Button
                        type="button"
                        variant="outline"
                        disabled={uploadingImage}
                        onClick={() => document.getElementById('thumbnail-image-upload').click()}
                      >
                        {uploadingImage ? 'Uploading...' : 'Upload'}
                      </Button>
                      <input
                        id="thumbnail-image-upload"
                        type="file"
                        accept="image/*"
                        onChange={handleThumbnailUpload}
                        className="hidden"
                      />
                    </div>
                    {formData.thumbnailUrl && formData.thumbnailUrl.startsWith('data:image') && (
                      <p className="text-xs text-muted-foreground mt-1">✓ Compressed image ready</p>
                    )}
                  </div>
                  <div>
                    <Label htmlFor="tags">Tags (comma-separated)</Label>
                    <Input
                      id="tags"
                      value={formData.tags}
                      onChange={(e) => setFormData({...formData, tags: e.target.value})}
                      placeholder="branding, marketing, podcast"
                    />
                  </div>
                  <div>
                    <Label htmlFor="status">Status</Label>
                    <Select value={formData.status} onValueChange={(value) => setFormData({...formData, status: value})}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="draft">Draft</SelectItem>
                        <SelectItem value="published">Published</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <DialogFooter>
                    <Button type="submit">{editingVideo ? 'Update' : 'Create'}</Button>
                  </DialogFooter>
                </form>
              </DialogContent>
            </Dialog>
          </div>

          {loading ? (
            <div className="text-center py-12">
              <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-primary border-r-transparent"></div>
            </div>
          ) : (
            <>
              <div className="grid gap-4" data-testid="videos-list">
                {videos.map((video) => (
                  <Card key={video.id} className="p-4 bg-[hsl(var(--surface-2))] border-border">
                    <div className="flex items-start gap-4">
                      <img src={video.thumbnailUrl} alt="" className="w-32 h-20 object-cover rounded" />
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <Badge variant={video.status === 'published' ? 'default' : 'secondary'}>
                            {video.status}
                          </Badge>
                          <Badge variant="outline">{video.series}</Badge>
                          <Badge variant="outline">{video.platform}</Badge>
                        </div>
                        <h3 className="font-display text-lg font-semibold mb-1">{video.title}</h3>
                        <p className="text-sm text-muted-foreground line-clamp-2">{video.dek}</p>
                      </div>
                      <div className="flex items-center gap-2">
                        {video.status === 'published' && (
                          <Button size="sm" variant="ghost" asChild>
                            <Link to={`/videos/${video.slug}`} target="_blank">
                              <Eye className="h-4 w-4" />
                            </Link>
                          </Button>
                        )}
                        <Button size="sm" variant="ghost" onClick={() => handleEdit(video)} data-testid={`edit-video-${video.id}`}>
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => handleDelete(video.id)} data-testid={`delete-video-${video.id}`}>
                          <Trash2 className="h-4 w-4 text-destructive" />
                        </Button>
                      </div>
                    </div>
                  </Card>
                ))}
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="mt-8">
                  <Pagination>
                    <PaginationContent>
                      <PaginationItem>
                        <PaginationPrevious 
                          onClick={() => handlePageChange(currentPage - 1)}
                          className={currentPage === 1 ? 'pointer-events-none opacity-50' : 'cursor-pointer'}
                        />
                      </PaginationItem>
                      
                      {getPageNumbers()[0] > 1 && (
                        <>
                          <PaginationItem>
                            <PaginationLink onClick={() => handlePageChange(1)} className="cursor-pointer">
                              1
                            </PaginationLink>
                          </PaginationItem>
                          {getPageNumbers()[0] > 2 && (
                            <PaginationItem>
                              <PaginationEllipsis />
                            </PaginationItem>
                          )}
                        </>
                      )}
                      
                      {getPageNumbers().map(page => (
                        <PaginationItem key={page}>
                          <PaginationLink 
                            onClick={() => handlePageChange(page)}
                            isActive={currentPage === page}
                            className="cursor-pointer"
                          >
                            {page}
                          </PaginationLink>
                        </PaginationItem>
                      ))}
                      
                      {getPageNumbers()[getPageNumbers().length - 1] < totalPages && (
                        <>
                          {getPageNumbers()[getPageNumbers().length - 1] < totalPages - 1 && (
                            <PaginationItem>
                              <PaginationEllipsis />
                            </PaginationItem>
                          )}
                          <PaginationItem>
                            <PaginationLink onClick={() => handlePageChange(totalPages)} className="cursor-pointer">
                              {totalPages}
                            </PaginationLink>
                          </PaginationItem>
                        </>
                      )}
                      
                      <PaginationItem>
                        <PaginationNext 
                          onClick={() => handlePageChange(currentPage + 1)}
                          className={currentPage === totalPages ? 'pointer-events-none opacity-50' : 'cursor-pointer'}
                        />
                      </PaginationItem>
                    </PaginationContent>
                  </Pagination>
                </div>
              )}
            </>
          )}
        </main>
      </div>
    </>
  );
}
